import { NgModule, ErrorHandler } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';

import { APOLLO_OPTIONS, ApolloModule } from 'apollo-angular';
import { HttpLink } from 'apollo-angular/http';
import { ApolloClientOptions } from '@apollo/client/core';
import { ApolloLink } from '@apollo/client/link/core';
import { onError } from '@apollo/client/link/error';
import { setContext } from '@apollo/client/link/context';
import { InMemoryCache, NormalizedCacheObject } from '@apollo/client/core';

import { environment } from '../environments/environment';
import { OrderTransactionsResolver } from './orders/service/queries/get-order-payments.query';
import { OrderItemBasicResolver } from './orders/service/queries/order-basic.fragment';
import { OrderGroupedItemsResolver } from './orders/service/order.resolver';
import { SubscriptionBasicResolver } from './subscriptions/service/subscription.resolver';
import { CategoryCountResolver } from './admin/service/queries/get-storefront-categories.query';
import { ProductVariantResolver } from './admin/service/queries/package-product.fragment';
import { GraphQLError } from './shared/errors/graphql-error';
import { UnauthorizedError } from './shared/errors/unauthorized-error';
import { ParentCancellationCategoryResolver } from './subscriptions/service/queries/get-cancellation-reasons.query';

const baseUri = environment.apiBaseUrl.endsWith('/') ? environment.apiBaseUrl.slice(0, -1) : environment.apiBaseUrl;
const uri = `${baseUri}/graphql`;

export function createApollo(httpLink: HttpLink, errorHandler: ErrorHandler): ApolloClientOptions<NormalizedCacheObject> {
  const authLink = setContext(_ => {
    const token = sessionStorage.getItem('auth');
    return { headers: new HttpHeaders().set('Authorization', `Bearer ${token}`) };
  });

  const removeTypenameMiddleware = new ApolloLink((operation, forward) => {
    if (operation.variables) {
      operation.variables = stripTypenames(operation.variables, '__typename');
      return forward ? forward(operation) : null;
    }
  });

  const errLink = onError(({ networkError, operation }) => {
    // network errors and auth errors should be handled by component
    if (!networkError || networkError instanceof UnauthorizedError) {
      return;
    }
    const { operationName, variables } = operation;
    const err = new GraphQLError(operationName, variables);
    errorHandler.handleError(err);
  });

  return {
    link: ApolloLink.from([
      removeTypenameMiddleware,
      errLink,
      authLink.concat(httpLink.create({ uri })),
    ]),
    cache: new InMemoryCache({
      possibleTypes: {
        Product: ['SimpleProduct', 'PackageProduct'],
      }
    }),
    resolvers: [
      SubscriptionBasicResolver,
      OrderGroupedItemsResolver,
      OrderItemBasicResolver,
      OrderTransactionsResolver,
      CategoryCountResolver,
      ProductVariantResolver,
      ParentCancellationCategoryResolver
    ],
    defaultOptions: {
      query: {
        errorPolicy: 'all',
        fetchPolicy: 'no-cache'
      }
    }
  };
}

function stripTypenames(obj: any, propToDelete: string) {
  for (const property in obj) {
    if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
      delete obj.property;
      const newData = stripTypenames(obj[property], propToDelete);
      obj[property] = newData;
    } else {
      if (property === propToDelete) {
        delete obj[property];
      }
    }
  }
  return obj;
}

@NgModule({
  exports: [ApolloModule],
  providers: [
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [HttpLink, ErrorHandler],
    },
  ],
})
export class GraphQLModule { }
